/**
 * ShapeRenderer — pure SVG primitive for the 6 cycle-103-critical
 * node shapes. Returns a single SVG element (path / circle / rect) at
 * (cx, cy) with the corrected radius applied.
 *
 * Geometry mirrors the design handoff
 * (`~/Downloads/design_handoff_echelon_update/environment/detail.jsx:848-863`).
 *
 * Pure function — no state, no DOM events. The shell node component
 * (`NodeShapeRoot`) wraps this with hit-area + status dot + keyboard
 * affordances.
 */

import type { ReactElement, SVGProps } from 'react';
import { correctedRadius, type NodeShape } from '../areaScale';

interface ShapeRendererProps extends Omit<SVGProps<SVGElement>, 'cx' | 'cy'> {
  shape: NodeShape;
  cx: number;
  cy: number;
  /** Nominal (pre-AREA_SCALE) radius in SVG units. */
  radius: number;
}

export function ShapeRenderer({
  shape,
  cx,
  cy,
  radius,
  ...rest
}: ShapeRendererProps): ReactElement {
  const r = correctedRadius(radius, shape);

  if (shape === 'circle') {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        {...(rest as SVGProps<SVGCircleElement>)}
      />
    );
  }

  if (shape === 'roundsquare') {
    return (
      <rect
        x={cx - r}
        y={cy - r}
        width={r * 2}
        height={r * 2}
        rx={2}
        {...(rest as SVGProps<SVGRectElement>)}
      />
    );
  }

  if (shape === 'diamond') {
    const d = `M ${cx} ${cy - r} L ${cx + r} ${cy} L ${cx} ${cy + r} L ${cx - r} ${cy} Z`;
    return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
  }

  if (shape === 'pentagon') {
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 5; i++) {
      const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    const d = 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
    return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
  }

  if (shape === 'hex') {
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3;
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    const d = 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
    return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
  }

  if (shape === 'star') {
    // 5-point pentagram. Inner radius / outer radius = (3-√5)/2 ≈ 0.382
    // for the canonical regular pentagram.
    const innerRatio = (3 - Math.sqrt(5)) / 2;
    const outer = r;
    const inner = r * innerRatio;
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 10; i++) {
      const a = (i * Math.PI) / 5 - Math.PI / 2;
      const rr = i % 2 === 0 ? outer : inner;
      pts.push([cx + rr * Math.cos(a), cy + rr * Math.sin(a)]);
    }
    const d = 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
    return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
  }

  if (shape === 'octagon') {
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4 + Math.PI / 8;
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    const d = 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
    return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
  }

  // triangle (equilateral, point-up)
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 3; i++) {
    const a = (i * 2 * Math.PI) / 3 - Math.PI / 2;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  const d = 'M ' + pts.map((p) => p.join(' ')).join(' L ') + ' Z';
  return <path d={d} {...(rest as SVGProps<SVGPathElement>)} />;
}
